import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import logo from "assets/img/logo/logo.png";
import logoWhite from "assets/img/logo/logo-white.png";
import { contact, navItems } from "./nav-data";

// Main Rionick Studios header (all main pages). Transparent over the hero, then a
// floating glass bar once scrolled; always stays visible.
// `lightLogoSrc` (passed by pages with dark heroes) switches the top state to light text.
// Older props (top_bar, commonOffCanvas, header_solid, headerClass) are still accepted.

const isExternal = (item) => Boolean(item.href);

const MenuLink = ({ item, className, onClick, children }) =>
  isExternal(item) ? (
    <a className={className} href={item.href} target="_blank" rel="noreferrer" onClick={onClick}>
      {children}
    </a>
  ) : (
    <Link className={className} to={item.to} onClick={onClick}>
      {children}
    </Link>
  );

// True once the page is scrolled past the top (switches to the floating glass bar)
const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setScrolled(window.scrollY > 40);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return scrolled;
};

const MegaMenu = ({ id, item, onClose }) => (
  <div className={`rh-mega rh-mega--${item.menu}`} id={id} role="menu">
    <div className="rh-mega__grid">
      {item.items.map((entry) => (
        <MenuLink key={entry.title} item={entry} className="rh-mega__item" onClick={onClose}>
          <span className="rh-mega__icon">
            <i className={entry.icon}></i>
          </span>
          <span className="rh-mega__text">
            <strong>
              {entry.title}
              {isExternal(entry) && <i className="fa-regular fa-arrow-up-right" aria-label="(opens in a new tab)"></i>}
            </strong>
            <span>{entry.desc}</span>
          </span>
        </MenuLink>
      ))}
    </div>
    {item.menu === "services" ? (
      <Link to="/hall-of-fame" state={{ curtain: true }} className="rh-mega__feature" onClick={onClose}>
        <span className="rh-mega__feature-eyebrow">16+ brands · 147+ projects</span>
        <strong>Step into our Hall of Fame</strong>
        <span>See the brands we’ve created for</span>
        <i className="fa-regular fa-arrow-right"></i>
      </Link>
    ) : (
      <Link to="/contact-us" className="rh-mega__feature rh-mega__feature--demo" onClick={onClose}>
        <span className="rh-mega__feature-eyebrow">Free demo</span>
        <strong>See Ghar360 or ARRISE in action</strong>
        <span>Book a walkthrough with our team</span>
        <i className="fa-regular fa-arrow-right"></i>
      </Link>
    )}
  </div>
);

const MobileDrawer = ({ open, onClose, pathname }) => {
  const [section, setSection] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div className={`rh-drawer${open ? " is-open" : ""}`} role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={!open}>
        <div className="rh-drawer__head">
          <Link to="/" onClick={onClose} className="rh-drawer__logo">
            <img src={logo} alt="Rionick Studios" />
          </Link>
          <button ref={closeRef} type="button" className="rh-drawer__close" onClick={onClose} aria-label="Close menu">
            <i className="fa-regular fa-xmark"></i>
          </button>
        </div>
        <nav className="rh-drawer__nav" aria-label="Mobile">
          {navItems.map((item) =>
            item.items ? (
              <div key={item.title} className={`rh-drawer__group${section === item.menu ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="rh-drawer__link"
                  aria-expanded={section === item.menu}
                  onClick={() => setSection((s) => (s === item.menu ? null : item.menu))}
                >
                  {item.title}
                  <i className="fa-regular fa-chevron-down"></i>
                </button>
                <div className="rh-drawer__sub">
                  <div>
                    {item.items.map((entry) => (
                      <MenuLink key={entry.title} item={entry} className="rh-drawer__subitem" onClick={onClose}>
                        <span className="rh-mega__icon">
                          <i className={entry.icon}></i>
                        </span>
                        <span>
                          <strong>
                            {entry.title}
                            {isExternal(entry) && <i className="fa-regular fa-arrow-up-right"></i>}
                          </strong>
                          <small>{entry.desc}</small>
                        </span>
                      </MenuLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.title}
                to={item.to}
                state={item.to === "/hall-of-fame" ? { curtain: true } : undefined}
                className={`rh-drawer__link${pathname === item.to ? " is-active" : ""}`}
                onClick={onClose}
              >
                {item.title}
              </Link>
            )
          )}
        </nav>
        <div className="rh-drawer__foot">
          <Link to="/contact-us" className="rh-btn rh-btn--primary rh-btn--block" onClick={onClose}>
            Book a Demo <i className="fa-regular fa-arrow-right"></i>
          </Link>
          <div className="rh-drawer__contact">
            <a href={contact.phoneHref}><i className="fa-regular fa-phone"></i> {contact.phone}</a>
            <a href={`mailto:${contact.email}`}><i className="fa-regular fa-envelope"></i> {contact.email}</a>
          </div>
          <div className="rh-drawer__social">
            <a href="https://www.instagram.com/rionick.studios/" target="_blank" rel="noreferrer" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
            <a href="https://www.linkedin.com/company/rionick-studios/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in"></i></a>
            <a href="https://www.youtube.com/@rionickstudios" target="_blank" rel="noreferrer" aria-label="YouTube"><i className="fa-brands fa-youtube"></i></a>
            <a href="https://www.facebook.com/rionickstudios" target="_blank" rel="noreferrer" aria-label="Facebook"><i className="fa-brands fa-facebook-f"></i></a>
            <a href={contact.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp"><i className="fa-brands fa-whatsapp"></i></a>
          </div>
        </div>
      </div>
      <button type="button" className={`rh-scrim${open ? " is-open" : ""}`} aria-label="Close menu" tabIndex={open ? 0 : -1} onClick={onClose}></button>
    </>
  );
};

const HeaderSix = ({ logoSrc = logo, lightLogoSrc }) => {
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  const [openMenu, setOpenMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeTimer = useRef(null);
  const onDark = Boolean(lightLogoSrc) && !scrolled;

  // Close menus on navigation
  useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
  }, [pathname]);

  // Esc closes an open mega menu
  useEffect(() => {
    if (!openMenu) return undefined;
    const onKey = (event) => event.key === "Escape" && setOpenMenu(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu]);

  const open = (menu) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(menu);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 160);
  };
  const closeDrawer = React.useCallback(() => setDrawerOpen(false), []);

  const sectionActive = (item) => item.items?.some((entry) => entry.to && pathname.startsWith(entry.to));

  return (
    <>
      <header
        className={`rh-header${scrolled ? " is-scrolled" : ""}${onDark ? " is-on-dark" : ""}`}
      >
        <div className="rh-header__bar">
          <Link to="/" className="rh-header__logo" aria-label="Rionick Studios home">
            <img src={onDark ? lightLogoSrc || logoWhite : logoSrc} alt="Rionick Studios" />
          </Link>

          <nav className="rh-nav" aria-label="Main">
            <ul>
              {navItems.map((item) =>
                item.items ? (
                  <li
                    key={item.title}
                    className={`rh-nav__item${openMenu === item.menu ? " is-open" : ""}`}
                    onMouseEnter={() => open(item.menu)}
                    onMouseLeave={scheduleClose}
                  >
                    <button
                      type="button"
                      className={`rh-nav__link${sectionActive(item) ? " is-active" : ""}`}
                      aria-expanded={openMenu === item.menu}
                      aria-controls={`rh-mega-${item.menu}`}
                      onClick={() => setOpenMenu((m) => (m === item.menu ? null : item.menu))}
                    >
                      {item.title}
                      <i className="fa-regular fa-chevron-down"></i>
                    </button>
                    <MegaMenu id={`rh-mega-${item.menu}`} item={item} onClose={() => setOpenMenu(null)} />
                  </li>
                ) : (
                  <li key={item.title} className="rh-nav__item">
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      state={item.to === "/hall-of-fame" ? { curtain: true } : undefined}
                      className={({ isActive }) => `rh-nav__link${isActive ? " is-active" : ""}`}
                    >
                      {item.title}
                    </NavLink>
                  </li>
                )
              )}
            </ul>
          </nav>

          <div className="rh-header__actions">
            <a
              className="rh-icon-btn rh-icon-btn--whatsapp"
              href={contact.whatsapp}
              target="_blank"
              rel="noreferrer"
              aria-label="Chat on WhatsApp"
              title="Chat on WhatsApp"
            >
              <i className="fa-brands fa-whatsapp"></i>
            </a>
            <Link to="/contact-us" className="rh-btn rh-btn--primary rh-header__cta">
              Book a Demo
            </Link>
            <button
              type="button"
              className="rh-burger"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>
      <MobileDrawer open={drawerOpen} onClose={closeDrawer} pathname={pathname} />
    </>
  );
};

export default HeaderSix;
